/**
 * @fileoverview Hook principal para sistema modular de features
 * @module hooks/useFeatureUnlock
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  doc, 
  onSnapshot, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useActiveFamily } from '../context/ActiveFamilyContext';
import { 
  FamilyFeatures, 
  FeatureMetrics, 
  FeatureName,
  UseFeatureUnlockReturn,
  DEFAULT_FAMILY_FEATURES,
  DEFAULT_METRICS
} from '../types/features';
import { calculateMetrics, invalidateMetricsCache } from '../services/featureMetricsService';
import { evaluateAllFeatures, getNewlyUnlockedFeatures } from '../utils/featureEvaluation';
import { showUnlockToast } from '../components/features/UnlockToast';
import { usePlanLimits } from './usePlanLimits';

// Estado global para el modal de invitación
let modalState: {
  isOpen: boolean;
  featureName: 'advancedTasks' | 'advancedActivities' | null;
  listeners: Set<(state: any) => void>;
} = {
  isOpen: false,
  featureName: null,
  listeners: new Set()
};

export function openFeatureUnlockedModal(featureName: 'advancedTasks' | 'advancedActivities') {
  modalState.isOpen = true;
  modalState.featureName = featureName;
  modalState.listeners.forEach(listener => listener({ ...modalState }));
}

export function closeFeatureUnlockedModal() {
  modalState.isOpen = false;
  modalState.featureName = null;
  modalState.listeners.forEach(listener => listener({ ...modalState }));
}

export function useFeatureUnlockedModalState() {
  const [state, setState] = useState(modalState);
  
  useEffect(() => {
    modalState.listeners.add(setState);
    return () => {
      modalState.listeners.delete(setState);
    };
  }, []);
  
  return state;
}

/**
 * Hook para gestionar features modulares de una familia
 */
export const useFeatureUnlock = (): UseFeatureUnlockReturn => {
  const { activeFamily } = useActiveFamily();
  const [features, setFeatures] = useState<FamilyFeatures>(DEFAULT_FAMILY_FEATURES);
  const [metrics, setMetrics] = useState<FeatureMetrics>(DEFAULT_METRICS);
  const [loading, setLoading] = useState(true);
  const { isFeatureBlocked, loading: planLoading } = usePlanLimits();
  
  // Referencia para evitar mostrar toasts duplicados
  const previousFeaturesRef = useRef<FamilyFeatures | null>(null);
  const notifiedFeaturesRef = useRef<Set<FeatureName>>(new Set());

  /**
   * Listener principal en tiempo real
   */
  useEffect(() => {
    if (!activeFamily?.id) {
      setLoading(false);
      setFeatures(DEFAULT_FAMILY_FEATURES);
      setMetrics(DEFAULT_METRICS);
      return;
    }

    setLoading(true);

    const unsubscribe = onSnapshot(
      doc(db, 'families', activeFamily.id),
      async (snap) => {
        try {
          if (!snap.exists()) {
            setFeatures(DEFAULT_FAMILY_FEATURES);
            setMetrics(DEFAULT_METRICS);
            setLoading(false);
            return;
          }

          const data = snap.data();
          const savedFeatures = data?.unlockedFeatures || {};

          // Calcular métricas actuales
          const currentMetrics = await calculateMetrics(activeFamily.id);
          setMetrics(currentMetrics);

          // Evaluar estado de cada feature
          let evaluatedFeatures = evaluateAllFeatures(savedFeatures, currentMetrics);

          // Plan gating: si el plan permite una feature, considerarla desbloqueada.
          // Mantener el toggle enabled del usuario si existe; si no existe, habilitar por default.
          if (!planLoading) {
            const planAllows: Partial<Record<FeatureName, boolean>> = {
              templateLibrary: !isFeatureBlocked('templates' as any),
              calendar: !isFeatureBlocked('calendar' as any),
              groupGoals: !isFeatureBlocked('goals' as any),
              advancedRecipes: !isFeatureBlocked('recipes' as any),
              mealPlanCalendar: !isFeatureBlocked('mealPlans' as any),
              shoppingList: !isFeatureBlocked('shoppingLists' as any),
              gritiAI: !isFeatureBlocked('ai' as any),
            };

            evaluatedFeatures = { ...evaluatedFeatures };
            (Object.keys(planAllows) as FeatureName[]).forEach((featureName) => {
              const allowed = planAllows[featureName];
              if (!allowed) return;

              const prev = evaluatedFeatures[featureName];
              const saved = (savedFeatures as any)?.[featureName];
              const hasSavedEnabled = saved && typeof saved.enabled === 'boolean';
              evaluatedFeatures[featureName] = {
                ...prev,
                unlocked: true,
                unlockedBy: prev.unlockedBy || 'manual',
                enabled: hasSavedEnabled ? Boolean(saved.enabled) : true,
              };
            });
          }
          
          // Detectar features recién desbloqueadas
          const newlyUnlocked = getNewlyUnlockedFeatures(
            evaluatedFeatures, 
            previousFeaturesRef.current
          );

          // Actualizar estado
          setFeatures(evaluatedFeatures);
          previousFeaturesRef.current = evaluatedFeatures;

          // Auto-guardar features desbloqueadas automáticamente
          if (newlyUnlocked.length > 0) {
            await autoSaveUnlockedFeatures(activeFamily.id, evaluatedFeatures, savedFeatures);
            
            // Mostrar notificaciones
            for (const featureName of newlyUnlocked) {
              // Evitar duplicados
              if (!notifiedFeaturesRef.current.has(featureName)) {
                // Mostrar modal para advancedTasks o advancedActivities
                if (featureName === 'advancedTasks' || featureName === 'advancedActivities') {
                  openFeatureUnlockedModal(featureName);
                } else {
                  showUnlockToast(featureName);
                }
                notifiedFeaturesRef.current.add(featureName);
                
                // Marcar como notificado en Firestore
                try {
                  await updateDoc(doc(db, 'families', activeFamily.id), {
                    [`unlockedFeatures.${featureName}.notificationShown`]: true
                  });
                } catch (notifError) {
                  console.error('Error updating notification status:', notifError);
                }
              }
            }
          }

          setLoading(false);
        } catch (error) {
          console.error('Error in useFeatureUnlock listener:', error);
          // Establecer valores por defecto en caso de error
          setFeatures(DEFAULT_FAMILY_FEATURES);
          setMetrics(DEFAULT_METRICS);
          setLoading(false);
        }
      },
      (error) => {
        console.error('Error in Firestore listener:', error);
        setFeatures(DEFAULT_FAMILY_FEATURES);
        setMetrics(DEFAULT_METRICS);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
      // Limpiar referencias al desmontar
      previousFeaturesRef.current = null;
      notifiedFeaturesRef.current.clear();
    };
  }, [activeFamily?.id, isFeatureBlocked, planLoading]);

  /**
   * Desbloquea una feature manualmente
   */
  const unlockFeature = useCallback(async (featureName: FeatureName) => {
    if (!activeFamily?.id) return;

    try {
      await updateDoc(doc(db, 'families', activeFamily.id), {
        [`unlockedFeatures.${featureName}`]: {
          unlocked: true,
          enabled: true,
          unlockedAt: serverTimestamp(),
          unlockedBy: 'manual',
          notificationShown: true // No mostrar toast en unlock manual
        }
      });

      // Invalidar cache de métricas
      invalidateMetricsCache(activeFamily.id);
    } catch (error) {
      console.error('Error unlocking feature:', error);
      throw error;
    }
  }, [activeFamily?.id]);

  /**
   * Activa/desactiva una feature en la UI
   */
  const toggleFeature = useCallback(async (featureName: FeatureName) => {
    if (!activeFamily?.id) return;

    const currentState = features[featureName];
    
    // No se puede activar si no está desbloqueada
    if (!currentState.unlocked) {
      console.warn(`Feature ${featureName} is not unlocked yet`);
      return;
    }

    try {
      await updateDoc(doc(db, 'families', activeFamily.id), {
        [`unlockedFeatures.${featureName}.enabled`]: !currentState.enabled
      });
    } catch (error) {
      console.error('Error toggling feature:', error);
      throw error;
    }
  }, [activeFamily?.id, features]);

  /**
   * Verifica si una feature está habilitada en UI
   */
  const isFeatureEnabled = useCallback((featureName: FeatureName): boolean => {
    return features[featureName]?.enabled || false;
  }, [features]);

  /**
   * Verifica si una feature está desbloqueada
   */
  const isFeatureUnlocked = useCallback((featureName: FeatureName): boolean => {
    return features[featureName]?.unlocked || false;
  }, [features]);

  return {
    features,
    metrics,
    loading,
    unlockFeature,
    toggleFeature,
    isFeatureEnabled,
    isFeatureUnlocked
  };
};

/**
 * Auto-guarda features que se acaban de desbloquear
 */
async function autoSaveUnlockedFeatures(
  familyId: string,
  evaluatedFeatures: FamilyFeatures,
  savedFeatures: any
): Promise<void> {
  const updates: any = {};
  
  for (const key in evaluatedFeatures) {
    const featureName = key as keyof FamilyFeatures;
    const current = evaluatedFeatures[featureName];
    const saved = savedFeatures[featureName];
    
    // Solo guardar si se acaba de desbloquear automáticamente
    if (
      current.unlocked && 
      current.unlockedBy === 'auto' && 
      !saved?.unlocked
    ) {
      updates[`unlockedFeatures.${featureName}`] = {
        unlocked: true,
        enabled: true, // Default habilitado
        unlockedAt: serverTimestamp(),
        unlockedBy: 'auto',
        notificationShown: false
      };
    }
  }
  
  // Guardar en batch si hay cambios
  if (Object.keys(updates).length > 0) {
    await updateDoc(doc(db, 'families', familyId), updates);
  }
}
